import { MAXIMUM_AMOUNT } from '@/constants';

/**
 * Copy for the information sheets (GIFTWEB-32 AC 6, AC 7), lifted from the
 * design's `infoSections` map.
 *
 * It lives apart from the component because it is pure copy a compliance
 * reviewer may want to read without picking through JSX, and because two of the
 * three quote regulated facts (the FSCS limit, the ISA allowance) that will be
 * edited far more often than the sheet that renders them.
 */

export type InfoSheetKey = 'how' | 'protected' | 'allowance';

export type InfoSection = {
  /** Omitted where the design runs the body without a heading. */
  head?: string;
  body: string;
};

export type InfoSheetContent = {
  title: string;
  sections: InfoSection[];
  /**
   * The partner strip under the body. Only "Your money is protected" carries
   * it — the sheet names Seccl, Fidelity and Aviva, so it shows them (AC 7).
   */
  partners?: boolean;
  /**
   * A "Need help?" button under "Got it" (AC 8). Only the allowance sheet has
   * one: it is the sheet that ends by telling the contributor to ask us, and
   * the question it raises — how much allowance is left — is one only the
   * child's parent or our team can answer.
   */
  support?: boolean;
};

const maxAmountLabel = `£${MAXIMUM_AMOUNT.toLocaleString('en-GB')}`;

/**
 * The child's first name reads better than "their" but is not always known —
 * the gift context can be missing on a cold entry.
 */
function possessive(childFirstName?: string): string {
  const name = childFirstName?.trim();
  return name ? `${name}'s` : 'their';
}

export function getInfoSheet(
  key: InfoSheetKey,
  childFirstName?: string,
): InfoSheetContent {
  switch (key) {
    case 'how':
      return {
        title: 'How does Mia work?',
        sections: [
          {
            body: `You gift into ${possessive(childFirstName)} Junior ISA. The money is invested for the long term and grows until they turn 18, when it becomes theirs to use for whatever comes next.`,
          },
        ],
      };

    case 'protected':
      return {
        title: 'Your money is protected',
        partners: true,
        sections: [
          {
            head: 'Held separately from Mia',
            body: "Your money never sits with Mia. It's held by Seccl, an FCA-regulated custodian (part of Octopus Group) that safeguards over £12bn.",
          },
          {
            head: 'Ring-fenced and yours',
            body: "It's kept legally separate from both Mia and Seccl, so it stays yours even if either firm fails, and can never be used to cover anyone else's losses.",
          },
          {
            head: 'FSCS backstop',
            body: 'The UK government-backed FSCS can pay up to £85,000 if a regulated firm fails and money is missing. This covers firm failure, not normal market ups and downs.',
          },
        ],
      };

    case 'allowance':
      return {
        title: 'How much can I gift?',
        support: true,
        sections: [
          {
            body: `A child can receive up to ${maxAmountLabel} across their Junior Cash ISA and Junior Stocks & Shares ISAs this tax year. If you're making a larger gift, we recommend checking with the parent or guardian that there's enough allowance remaining.`,
          },
          {
            body: 'If the annual allowance has already been used, your gift may need to be returned. Not sure? Click Need help? and we’d love to hear from you.',
          },
        ],
      };
  }
}
